import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
  Switch,
  Checkbox,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import axios from "../../api";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AddState from "./States/AddState";
import AddCity from "./Cities/AddCity";
import AddRegion from "./Regions/AddRegion";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import Locations from "examples/ReuseFunctions/Locations";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface SelectedRow {
  dataIndex: number;
}
interface CityData {
  city_id: number;
  city_name: string;
  state_id: number;
  is_active: number;
}
interface StateData {
  state_name: number;
  state_id: number;
  is_active: number;
}
interface RegionData {
  region_name: number;
  region_id: number;
  is_active: number;
  city_id: number;
}
interface ColumnMeta {
  label?: string;
  name: string;
  index: number;
}
interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: StateData[];
}
interface TableMetaCity {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: CityData[];
}
interface TableMetaRegion {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: RegionData[];
}

// Extracted components - OUTSIDE the main component
interface CheckboxHeaderCellProps {
  statesData: StateData[] | null;
  selectedRows: SelectedRow[];
  onCheckAll: (checked: boolean) => void;
}

const CheckboxHeaderCell: React.FC<CheckboxHeaderCellProps> = ({
  statesData,
  selectedRows,
  onCheckAll,
}) => {
  return (
    <th
      style={{
        padding: "16px",
        textAlign: "left",
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Checkbox
        checked={
          !!statesData &&
          statesData.length > 0 &&
          selectedRows.length === statesData.length
        }
        onChange={(event) => onCheckAll(event.target.checked)}
      />
    </th>
  );
};

interface CheckboxBodyCellProps {
  rowIndex: number;
  selectedRows: SelectedRow[];
  onCheckboxChange: (rowIndex: number) => void;
}

const CheckboxBodyCell: React.FC<CheckboxBodyCellProps> = ({
  rowIndex,
  selectedRows,
  onCheckboxChange,
}) => {
  return (
    <Checkbox
      checked={selectedRows.some((row) => row.dataIndex === rowIndex)}
      onChange={() => onCheckboxChange(rowIndex)}
    />
  );
};

interface StateStatusCellProps {
  value: number;
  data: StateData;
  onStatusChange: (data: StateData, isActive: boolean) => void;
}

const StateStatusCell: React.FC<StateStatusCellProps> = ({
  value,
  data,
  onStatusChange,
}) => {
  return (
    <Switch
      checked={!!value}
      onChange={(event) => onStatusChange(data, event.target.checked)}
      color="primary"
    />
  );
};

interface StatusBadgeCellProps {
  value: boolean | number;
}

const StatusBadgeCell: React.FC<StatusBadgeCellProps> = ({ value }) => {
  return (
    <span
      className="active-btn"
      style={{
        borderColor: value ? "#00bdaa" : "#fe346e",
      }}
    >
      {value ? "Active" : "Inactive"}
    </span>
  );
};

interface CityActionsCellProps {
  cityData: CityData;
  onStatusChange: (data: CityData, statusData: number) => void;
}

const CityActionsCell: React.FC<CityActionsCellProps> = ({
  cityData,
  onStatusChange,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Button
        className="make-active-btn"
        sx={{ position: "relative" }}
        size="small"
        variant="contained"
        onClick={() => {
          const statusData = cityData?.is_active ? 0 : 1;
          onStatusChange(cityData, statusData);
        }}
      >
        {cityData?.is_active ? "Make InActive" : "Make Active"}
      </Button>
    </Box>
  );
};

interface RegionActionsCellProps {
  regionData: RegionData;
  onStatusChange: (data: RegionData, statusData: number) => void;
}

const RegionActionsCell: React.FC<RegionActionsCellProps> = ({
  regionData,
  onStatusChange,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Button
        className="make-active-btn"
        sx={{
          position: "relative",
        }}
        size="small"
        variant="contained"
        onClick={() => {
          const statusData = regionData?.is_active ? 0 : 1;
          onStatusChange(regionData, statusData);
        }}
      >
        {regionData?.is_active ? "Make InActive" : "Make Active"}
      </Button>
    </Box>
  );
};

// Render helper functions - OUTSIDE the component
const renderCheckboxHeaderCell = (
  columnMeta: ColumnMeta,
  statesData: StateData[] | null,
  selectedRows: SelectedRow[],
  handleCheckAllboxes: (checked: boolean) => void
) => {
  return (
    <CheckboxHeaderCell
      statesData={statesData}
      selectedRows={selectedRows}
      onCheckAll={handleCheckAllboxes}
    />
  );
};

const renderCheckboxBodyCell = (
  tableMeta: TableMeta,
  selectedRows: SelectedRow[],
  handleCheckboxChange: (rowIndex: number) => void
) => {
  return (
    <CheckboxBodyCell
      rowIndex={tableMeta.rowIndex}
      selectedRows={selectedRows}
      onCheckboxChange={handleCheckboxChange}
    />
  );
};

const renderStateStatusCell = (
  value: number,
  tableMeta: TableMeta,
  handleStateStatus: (data: StateData, isActive: boolean) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return (
    <StateStatusCell
      value={value}
      data={data}
      onStatusChange={handleStateStatus}
    />
  );
};

const renderStatusBadgeCell = (value: boolean | number) => {
  return <StatusBadgeCell value={value} />;
};

const renderCityActionsCell = (
  value: number,
  tableMeta: TableMetaCity,
  handleCityStatus: (data: CityData, statusData: number) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const cityData = tableMeta.tableData[currentTableData[rowIndex]?.index];
  return (
    <CityActionsCell cityData={cityData} onStatusChange={handleCityStatus} />
  );
};

const renderRegionActionsCell = (
  value: number,
  tableMeta: TableMetaRegion,
  handleRegionStatus: (data: RegionData, statusData: number) => void
) => {
  const { currentTableData, rowIndex } = tableMeta;
  const regionData = tableMeta?.tableData[currentTableData[rowIndex]?.index];
  return (
    <RegionActionsCell
      regionData={regionData}
      onStatusChange={handleRegionStatus}
    />
  );
};

const Profile = () => {
  const [statesData, setStatesData] = useState<StateData[] | null>(null);
  const [citiesData, setCitiesData] = useState<CityData[] | null>(null);
  const [areasData, setAreasData] = useState<RegionData[] | null>(null);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [selectedRows, setSelectedRows] = React.useState<SelectedRow[]>([]);
  const {
    states,
    setSelectedState,
    setSelectedCity,
    handleStates,
    handleCities,
  } = Locations();

  const fetchStates = async () => {
    try {
      const response = await axios.get("/states");
      const { data } = response;
      setStatesData(data);
    } catch (error) {
      console.error("Fetching states failed:", error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`/cities`);
      const { data } = response;
      setCitiesData(data);
    } catch (error) {
      console.error("Fetching cities failed:", error);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`/regions`);
      const { data } = response;
      setAreasData(data);
    } catch (error) {
      console.error("Fetching areas failed:", error);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchCities();
    fetchAreas();
  }, []);

  const [tabIndex, setTabIndex] = useState(0);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleStateClose = () => {
    setIsStateOpen(false);
  };

  const handleStateOpen = () => {
    setIsStateOpen(true);
  };

  const handleCityClose = () => {
    setIsCityOpen(false);
  };

  const handleCityOpen = () => {
    handleStates();
    setIsCityOpen(true);
  };

  const handleRegionClose = () => {
    setIsRegionOpen(false);
  };

  const handleRegionOpen = () => {
    handleStates();
    handleCities(null);
    fetchCities();
    setIsRegionOpen(true);
  };

  const handleStateStatus = async (data: StateData, isActive: boolean) => {
    try {
      const stateResponse = await axios.put(`/states/${data?.state_id}`, {
        is_active: isActive,
      });
      fetchStates();
      fetchCities();
      handleStates();
      fetchSuccess(stateResponse.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleCityStatus = async (data: CityData, isActive: number) => {
    try {
      const cityResponse = await axios.put(`/cities/${data?.city_id}`, {
        is_active: isActive,
        state_id: data?.state_id,
      });
      fetchCities();
      fetchAreas();
      handleCities(null);
      fetchSuccess(cityResponse.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleRegionStatus = async (data: RegionData, isActive: number) => {
    try {
      const regionResponse = await axios.put(`/regions/${data?.region_id}`, {
        is_active: isActive,
        city_id: data?.city_id,
      });
      fetchAreas();
      fetchSuccess(regionResponse.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleBulkStateUpdate = async (activeData: boolean) => {
    if (!statesData) return;
    try {
      const promises = selectedRows.map(async (row) => {
        const state = statesData[row.dataIndex];
        return axios.put(`/states/${state?.state_id}`, {
          is_active: activeData,
        });
      });
      await Promise.all(promises);
      setSelectedRows([]);
      fetchStates();
      handleStates();
      fetchSuccess(activeData ? "States activated" : "States deactivated");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleCheckAllboxes = (checked: boolean): void => {
    if (!statesData) return;

    setSelectedRows(
      checked ? statesData.map((_, index) => ({ dataIndex: index })) : []
    );
  };

  const handleCheckboxChange = (rowIndex: number) => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.some(
        (row) => row.dataIndex === rowIndex
      );
      return isSelected
        ? prevSelectedRows.filter((row) => row.dataIndex !== rowIndex)
        : [...prevSelectedRows, { dataIndex: rowIndex }];
    });
  };

  // Fixed: Extract nested ternary into a function
  const handleAddButtonClick = () => {
    if (tabIndex === 0) {
      handleStateOpen();
    } else if (tabIndex === 1) {
      handleCityOpen();
    } else {
      handleRegionOpen();
    }
  };

  // Fixed: Extract nested ternary for button label
  const getAddButtonLabel = () => {
    if (tabIndex === 1) {
      return "Add City";
    }
    return "Add Region";
  };

  if (!statesData && !citiesData && !areasData) {
    return <PageLoader />;
  }

  const stateColumns = [
    {
      name: "checkbox",
      label: "",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (columnMeta: ColumnMeta) =>
          renderCheckboxHeaderCell(
            columnMeta,
            statesData,
            selectedRows,
            handleCheckAllboxes
          ),
        customBodyRender: (value: any, tableMeta: TableMeta) =>
          renderCheckboxBodyCell(tableMeta, selectedRows, handleCheckboxChange),
      },
    },
    { name: "state_name", label: "State Name" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: (value: number, tableMeta: TableMeta) =>
          renderStateStatusCell(value, tableMeta, handleStateStatus),
      },
    },
  ];

  const cityColumns = [
    { name: "state_name", label: "State Name" },
    { name: "city_name", label: "City Name" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusBadgeCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      align: "center",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: number, tableMeta: TableMetaCity) =>
          renderCityActionsCell(value, tableMeta, handleCityStatus),
      },
    },
  ];

  const regionColumns = [
    { name: "state_name", label: "State Name" },
    { name: "city_name", label: "City Name" },
    { name: "region_name", label: "Region Name" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusBadgeCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      align: "center",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: number, tableMeta: TableMetaRegion) =>
          renderRegionActionsCell(value, tableMeta, handleRegionStatus),
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card
        sx={{
          maxWidth: "100vw",
          width: "100%",
          padding: "24px",
          maxHeight: "70%",
        }}
      >
        <SoftBox mt={5}>
          <Grid justifyContent="center">
            <Grid item xs={12} md={8} lg={6}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                marginBottom={2}
              >
                <Grid item>
                  <Typography variant="h5" component="div" gutterBottom>
                    Locations
                  </Typography>
                </Grid>

                <Grid item>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    {tabIndex !== 0 && (
                      <Button
                        className="success-btn"
                        type="submit"
                        variant="contained"
                        onClick={handleAddButtonClick}
                        color="success"
                        sx={{ marginRight: "10px" }}
                      >
                        {getAddButtonLabel()}
                      </Button>
                    )}
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      {tabIndex === 0 && (
                        <>
                          <Button
                            variant="contained"
                            className="success-btn"
                            onClick={() => {
                              handleBulkStateUpdate(true);
                            }}
                            disabled={selectedRows.length === 0}
                            sx={{ marginRight: "10px" }}
                          >
                            Activate
                          </Button>
                          <Button
                            variant="contained"
                            className="success-btn"
                            onClick={() => {
                              handleBulkStateUpdate(false);
                            }}
                            disabled={selectedRows.length === 0}
                          >
                            InActivate
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Card>
                <CardContent className="profile-tabs">
                  <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="States" />
                    <Tab label="Cities" />
                    <Tab label="Regions" />
                  </Tabs>
                  <Box mt={3}>
                    {tabIndex === 0 && (
                      <SoftBox
                        sx={{
                          "& .MuiTableRow-root:not(:last-child)": {
                            "& td": {
                              borderBottom: ({
                                borders: { borderWidth, borderColor },
                              }: any) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            },
                          },
                        }}
                        className="m-states"
                      >
                        <ThemeProvider theme={getMuiTheme}>
                          <MUIDataTable
                            title={"Manage States"}
                            data={statesData || []}
                            columns={stateColumns}
                            options={tableOptions}
                          />
                        </ThemeProvider>
                      </SoftBox>
                    )}
                    {tabIndex === 1 && (
                      <SoftBox
                        sx={{
                          "& .MuiTableRow-root:not(:last-child)": {
                            "& td": {
                              borderBottom: ({
                                borders: { borderWidth, borderColor },
                              }: any) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            },
                          },
                        }}
                        className="m-cities"
                      >
                        <ThemeProvider theme={getMuiTheme}>
                          <MUIDataTable
                            title={"Manage Cities"}
                            data={citiesData || []}
                            columns={cityColumns}
                            options={tableOptions}
                          />
                        </ThemeProvider>
                      </SoftBox>
                    )}
                    {tabIndex === 2 && (
                      <SoftBox
                        sx={{
                          "& .MuiTableRow-root:not(:last-child)": {
                            "& td": {
                              borderBottom: ({
                                borders: { borderWidth, borderColor },
                              }: any) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            },
                          },
                        }}
                        className="m-regions"
                      >
                        <ThemeProvider theme={getMuiTheme}>
                          <MUIDataTable
                            title={"Manage Regions"}
                            data={areasData || []}
                            columns={regionColumns}
                            options={tableOptions}
                          />
                        </ThemeProvider>
                      </SoftBox>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </Card>

      {isStateOpen && (
        <AddState
          isOpen={isStateOpen}
          onClose={handleStateClose}
          setSelectedState={setSelectedState}
          fetchStates={fetchStates}
          handleStates={handleStates}
        />
      )}
      {isCityOpen && (
        <AddCity
          isOpen={isCityOpen}
          onClose={handleCityClose}
          states={states ?? []}
          setSelectedState={setSelectedState}
          fetchCities={fetchCities}
          handleStates={handleCities}
        />
      )}
      {isRegionOpen && (
        <AddRegion
          isOpen={isRegionOpen}
          onClose={handleRegionClose}
          states={states ?? []}
          cities={citiesData || []}
          setSelectedState={setSelectedState}
          setSelectedCity={setSelectedCity}
          fetchAreas={fetchAreas}
        />
      )}
    </DashboardLayout>
  );
};

export default Profile;
